import React, {useState, useEffect} from "react";
import {VsiBubble, VsiH4, VsiSmallText, VsiSpanText} from "../../../styled/App.styled";
import {useTitleFromComponent, useDataWithRoute, useAuthWithRoute} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {Button, Card, Tooltip} from "reactstrap";
import Stockchart from "../../../components/Stockchart";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {virtualTrends} from "../../../utils/gridColumnDefs/virtual/virtualTrends";
import {formatStockChartConfig} from "../../../utils/Helpers";
import styles from "../../../utils/themeStyles";
import {PostAuth, PutAuth} from "../../../utils/Verbs";

// Each Chart Type has different dropdown options
const VMs = {
    value: "vms", label: "VMs",
    chartDefs: [
        {
            name: "# of VMs",
            field: "num_vms"
        }
    ]
}

const VmBuildCapacity = {
    value: "vm_build_capacity", label: "VM Build Capacity",
    chartDefs: [
        {
            name: "VM Build Capacity",
            field: "vm_build_capacity"
        }
    ]
}

const CpuCap = {
    value: "cpu_capacity", label: "CPUs Capacity",
    chartDefs: [
        {
            name: "CPU Capacity",
            field: "cpu_capacity"
        }
    ]
}

const CpuUsed = {
    value: "cpu_used", label: "CPUs Used",
    chartDefs: [
        {
            name: "CPU Used",
            field: "cpu_used"
        }
    ]
}

const MemoryCap = {
    value: "memory", label: "Memory Capacity (GiB)",
    chartDefs: [
        {
            name: "Memory",
            field: "memory"
        }
    ]
}
const MemoryUsed = {
    value: "memory_used", label: "Memory Used (GiB)",
    chartDefs: [
        {
            name: "Memory Used",
            field: "memory_used"
        }
    ]
}
const DiskCap = {
    value: "disk", label: "Disk Capacity (GiB)",
    chartDefs: [
        {
            name: "Disk Capacity",
            field: "disk_capacity"
        }
    ]
}
const DiskUsed = {
    value: "disk_used", label: "Disk Used (GiB)",
    chartDefs: [
        {
            name: "Disk Used",
            field: "disk_used"
        }
    ]
}

const ClusterCount = {
    value: "num_clusters", label: "Cluster Count",
    chartDefs: [
        {
            name: "Cluster Count",
            field: "num_clusters"
        }
    ]
}
const HostCount = {
    value: "num_esx_hosts", label: "Host Count",
    chartDefs: [
        {
            name: "Host Count",
            field: "num_esx_hosts"
        }
    ]
}

const CpuRightSizeSavings = {
    value: "cpu_right_sized_savings", label: "Cpu Right-Size Savings",
    chartDefs: [
        {
            name: "Cpu Right-Size Savings",
            field: "cpu_right_sized_savings"
        }
    ]
}

const MemoryRightSizeSavings = {
    value: "memory_right_sized_savings", label: "Memory Right-Size Savings",
    chartDefs: [
        {
            name: "Memory Right-Size Savings",
            field: "memory_right_sized_savings"
        }
    ]
}

const DiskRightSizeSavings = {
    value: "disk_right_sized_savings", label: "Disk Right-Size Savings",
    chartDefs: [
        {
            name: "Disk Right-Size Savings",
            field: "disk_right_sized_savings"
        }
    ]
}

const UnderAllocatedVmsCount = {
    value: "under_allocated_vm_count", label: "Under-Allocated VMs", // score > 65
    chartDefs: [
        {
            name: "Under-Allocated Vms",
            field: "under_allocated_count"
        }
    ]
}

const OverAllocatedVmsCount = {
    value: "over_allocated_vm_count", label: "Over-Allocated VMs", // score < 15
    chartDefs: [
        {
            name: "Over-Allocated Vms",
            field: "over_allocated_count"
        }
    ]
}

const OverAllocatedDatastoresCount = {
    value: "over_allocated_ds_count", label: "Over-Allocated Datastores", // score < 15
    chartDefs: [
        {
            name: "Over-Allocated Datastores Count",
            field: "over_allocated_ds_count"
        }
    ]
}

const DatastoreAllocation = {
  value: "datastore_allocation",
  label: "Allocated",
  chartDefs: [
    {
      name: "Allocated",
      field: "datastore_allocation"
    }
  ]
}

const healthyTags = {
  value: "healthy_vms",
  label: "Healthy VMs",
  chartDefs: [
    {
      name: "# Healthy VMs",
      field: "num_vms"
    }
  ]
}

const unhealthyTags = {
  value: "unhealthy_vms",
  label: "Unhealthy VMs",
  chartDefs: [
    {
      name: "# Unhealthy VMs",
      field: "num_vms"
    }
  ]
}


// Assign the metrics to each Chart Type
const MajorMetrics = [
  VMs,
  VmBuildCapacity,
  CpuCap,
  CpuUsed,
  MemoryCap,
  MemoryUsed,
  DiskCap,
  DiskUsed,
  ClusterCount,
  HostCount,
  CpuRightSizeSavings,
  MemoryRightSizeSavings,
  DiskRightSizeSavings,
  UnderAllocatedVmsCount,
  OverAllocatedVmsCount,
  OverAllocatedDatastoresCount,
  healthyTags,
  unhealthyTags,
];
const metricMap = {
    HISTORY: MajorMetrics,
    VCENTER: MajorMetrics,
    DATACENTER: MajorMetrics,
    CLUSTER: MajorMetrics,
    DATASTORE: [
        VMs,
        DiskCap,
        DiskUsed,
        DatastoreAllocation
    ],
    STATE_TAG: MajorMetrics
}

const chartTypesConfig = [
    {
        group_by: "all_history", label: "History", drop_desc: "History", unit: "HISTORY",
        field: "all_history"
    },
    {
        group_by: "vcenter", label: "vCenters", drop_desc: "vCenter", unit: "VCENTER",
        field: "vcenter_name"
    },
    {
        group_by: "datacenter", label: "Data Centers", drop_desc: "Data Center", unit: "DATACENTER",
        field: "datacenter_name"
    },
    {
        group_by: "cluster_name", label: "Clusters", drop_desc: "Cluster", unit: "CLUSTER",
        field: "cluster_name"
    },
    {
        group_by: "datastore", label: "Datastores", drop_desc: "Datastore", unit: "DATASTORE",
        field: "datastore_name"
    },
    {
        group_by: "state_tag", label: "State Tags by VM", drop_desc: "Tag", unit: "STATE_TAG",
        field: "state_tag"
    }
];
const defaultCache = chartTypesConfig.reduce((acc, item) => ({...acc, [item.field]: []}), {});

const theAllTab = {value: `ALL`, label: "All"};
const defaultMetricNone = {
    value: "none", label: "None",
    chartDefs: [
        {
            name: "none",
            field: "none"
        }
    ]
};

const overflowInitialPicks = 5;
const stockChartLineLimit = 50;
const saveSelectionLimit = 20;

export default function VirtualTrends2({ context, title }) {
  useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();
  // Top Level Options
  const [chartType, setChartType] = useState(chartTypesConfig[0]);
  const [selectedResource, setSelectedResource] = useState(theAllTab);
  const [allResourceOptions, setAllResourceOptions] = useState([theAllTab]);
  // Metric Options Left / Right
  const [axis1Metric, setAxis1Metric] = useState(VMs);
  const [axis2Metric, setAxis2Metric] = useState(defaultMetricNone);
  // Cache
  const [typesCache, setTypesCache] = useState(defaultCache);
  // Display content
  const [formattedChartData, setFormattedChartData] = useState([]);
  const [chartConfig, setChartConfig] = useState({});
  const [gridData, setGridData] = useState([]);
  // Data
  const unitRoute = `/api/v6/report/enterprise_virtual_trend_by_unit_proc?client_id=${client.clientId}&period_id=${periodId}&unit=${chartType.unit}&unit_name=ALL`;
  const [mainData, { isLoading: isLoadingMainData }] = useDataWithRoute(unitRoute);
  // MultiSelection
  const [overflowTypeOptions, setOverflowTypeOptions] = useState([]);
  const [overflowType, setOverflowType] = useState([]);
  // Save Operations
  const [applyTooltipOpen, setApplyTooltipOpen] = useState(false);
  const [applyTooltipMessage, setApplyTooltipMessage] = useState(`Apply Saved ${chartType.drop_desc}s`);
  const [saveTooltipOpen, setSaveTooltipOpen] = useState(false);
  const [saveTooltipMessage, setSaveTooltipMessage] = useState(`Save List of ${chartType.drop_desc}s`);
  const [savedList, {reFetchData: reFetchSavedPlanData}] = useAuthWithRoute(`/api/v6/admin/virtual_trend_list/${user._id}/${client.clientId}/${chartType.unit}`);

    useEffect(() => {
        // When switch dates, reset the cache
        setTypesCache(defaultCache);
    }, [periodId]);

    useEffect(() => {
        // Whenever switch chart type, reset resource to All
        setSelectedResource(theAllTab);
        // Make sure Left Right axis only show acceptable choices
        if (!metricMap[chartType.unit].includes(axis1Metric)) {
            setAxis1Metric(metricMap[chartType.unit][0])
            setAxis2Metric(defaultMetricNone);
        }
        if (!metricMap[chartType.unit].includes(axis2Metric)) {
            setAxis2Metric(defaultMetricNone);
        }
    }, [chartType]);

    // avoiding infinite rerenders
    useEffect(() => {
        if (overflowTypeOptions.length) {
            setOverflowType(overflowTypeOptions.slice(0, overflowInitialPicks));
        }
    }, [overflowTypeOptions]);

    useEffect(() => {
        // Choose to use cache or use newly called data
        const isAllSelected = selectedResource.value === "ALL";

        let data = [...(mainData?.recordset ?? [])];
        if (!isLoadingMainData && typesCache[chartType.field].length === 0) {
            setTypesCache(prevState => ({
                ...prevState,
                [chartType.field]: data
            }))
        } else {
            data = [...typesCache[chartType.field]];
        }

        // Format stock chart and resource options
        let uniqueConfigs = {};
        let dtNames = [];
        let resourceUpdate = [theAllTab];
        let targetField = axis1Metric.chartDefs[0].field;
        let activeOverflow = overflowType.map(type => ({
            name: type.value,
            field: targetField,
            dataLabels: {format: "{point.y:,.0f}"},
            tooltip: {pointFormat: "{series.name}: <b>{point.y:,.0f}</b>"}
        }));

        data?.forEach(datum => {
            const dcg = datum[chartType.group_by];
            if (!uniqueConfigs[dcg]) {
                uniqueConfigs[dcg] = true;
                // SORTING
                // uniqueConfigs[dcg] = datum[targetField];
                dtNames.push({
                    name: dcg,
                    field: targetField,
                    dataLabels: {format: "{point.y:,.0f}"},
                    tooltip: {pointFormat: "{series.name}: <b>{point.y:,.0f}</b>"}
                });
                resourceUpdate.push({
                    value: dcg,
                    label: dcg === "null" ? "Unknown" : dcg
                });
            }
            // SORTING
            // uniqueConfigs[dcg] = Math.max(uniqueConfigs[dcg], datum[targetField]);
        });
        setAllResourceOptions(resourceUpdate);
        // filter when a resource is selected
        if (!isAllSelected) {
            data = data.filter(item => item[chartType.group_by] === selectedResource.value);
            activeOverflow = [{
                name: selectedResource.value,
                field: targetField,
                dataLabels: {format: "{point.y:,.0f}"},
                tooltip: {pointFormat: "{series.name}: <b>{point.y:,.0f}</b>"}
            }];
        }
        // SORTING: any comment marked with SORTING is to be determined later
        // dtNames.sort((a, b) => uniqueConfigs[a.name] - uniqueConfigs[b.name]);

        if (!activeOverflow.length) {
            activeOverflow = [...dtNames];
        }

    let axis1Data = [...data];
    if (axis1Metric.value === "healthy_vms" || axis1Metric.value === "unhealthy_vms") {
      axis1Data = axis1Data.filter(datum => datum?.state_tag?.toUpperCase?.() === axis1Metric.value.split('_')[0].toUpperCase());
    }

    setGridData(axis1Data);
    let config = formatStockChartConfig(axis1Data || [], activeOverflow, chartType.group_by);
    if (config.series.length > stockChartLineLimit) {
      // Hard limit to prevent freezing
      config.series = config.series.slice(0, stockChartLineLimit)
    }

    // Modify the legend only when history
    if (chartType.unit === 'HISTORY') {
      config.series = config.series.map((cs) => ({
        ...cs,
        name: `${axis1Metric.chartDefs[0].name}`,
      }))
    }

    // Update OverFlow Options
    if (isAllSelected && (!overflowTypeOptions.length || overflowTypeOptions?.[0]?.label !== dtNames?.[0]?.name)) {
        const allOverflow = dtNames.map(dt => ({
            label: dt.name,
            value: dt.name
        }));
        setOverflowTypeOptions(allOverflow);
    } else if (!isAllSelected) {
        setOverflowTypeOptions([]);
    }

    // When Right Axis is selected other than defaultNoneValue, generate second set of stock lines, then concat them
    if (axis2Metric.value !== 'none') {
      let targetFieldSec = axis2Metric.chartDefs[0].field;
      // Re-choose the field based on what we have on first AOF
      let activeOverflowSec = activeOverflow.map(aof => ({
        ...aof,
        field: targetFieldSec,
      }));
      if (!activeOverflowSec.length) {
        // In case overFlow is cleaned by user, rebuild names with second target fields
        const dtNamesSec = dtNames.map(dtn => ({...dtn, field: targetFieldSec}))
        activeOverflowSec = [...dtNamesSec];
      }

      let axis2Data = [...data];
      if (axis2Metric.value === "healthy_vms" || axis2Metric.value === "unhealthy_vms") {
        axis2Data = axis2Data.filter(datum => datum?.state_tag?.toUpperCase?.() === axis2Metric.value.split('_')[0].toUpperCase());
      }

      let config2 = formatStockChartConfig(axis2Data || [], activeOverflowSec, chartType.group_by);
      if (config2.series.length > stockChartLineLimit) {
        // Hard limit to prevent freezing
        config2.series = config2.series.slice(0, stockChartLineLimit)
      }
      // Modify the name field to have distinct legends
      config.series = config.series.map((cs) => ({
        ...cs,
        name: chartType.unit === 'HISTORY'
            ? axis1Metric.chartDefs[0].name
            : cs.name + `(${axis1Metric.chartDefs[0].name})`,
      }))
      config2.series = config2.series.map((cs) => ({
        ...cs,
        yAxis: 1,
        name: chartType.unit === 'HISTORY'
            ? axis2Metric.chartDefs[0].name
            : cs.name + `(${axis2Metric.chartDefs[0].name})`,
      }))
      config.series = [...config.series, ...config2.series]
    }

        setFormattedChartData(config.series);
        setChartConfig({
            yAxis: [
                {
                    title: {
                        text: axis1Metric.label,
                    }
                },
                {
                    title: {
                        text: axis2Metric.label,
                    },
                    opposite: true
                }
            ],
        });
    }, [mainData, selectedResource, axis1Metric, axis2Metric, overflowType]);

    useEffect(() => {
        applySavedSelection()
    }, [savedList]);

    useEffect(() => {
        // Apply selection when switching
        if (selectedResource.value === "ALL") {
            reFetchSavedPlanData()
        }
    }, [chartType, selectedResource]);

    // Generate non-repeat Left Right options
    const nonRepeatAxis1Metrics = metricMap[chartType.unit].filter(mt =>
        mt.value !== axis1Metric.value && mt.value !== axis2Metric.value);
    const nonRepeatAxis2Metrics = [defaultMetricNone, ...nonRepeatAxis1Metrics]

  const savePlan = async () => {
    let savedPlan = {
      user: user._id,
      clientId: client.clientId,
      dropDownType: chartType.unit,
      selectedList: overflowType
    };
    const token = await getAccessTokenSilently();
    if (savedList[0]?.selectedList.length > 0) {
      if (savedList[0].selectedList !== savedPlan.selectedList) {
        try {
          await PutAuth(`/api/v6/admin/virtual_trend_list/${savedList[0]._id}`, savedPlan, token);
          setSaveTooltipMessage('Saved!');
        } catch (err) {
          console.error(err);
        }
      }
    } else {
      try {
        await PostAuth(`/api/v6/admin/virtual_trend_list`, savedPlan, token);
        setSaveTooltipMessage('Saved!');
      } catch (err) {
        console.error(err);
      }
    }
    reFetchSavedPlanData()
  };

    const applySavedSelection = () => {
        if (savedList[0]?.selectedList) {
            setOverflowType(savedList[0]?.selectedList);
        }
    }

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Virtual Trends</VsiH4>
            <VsiBubble>
                <div className={"d-flex w-100"}>
                    <VsiLabeledSelect options={chartTypesConfig}
                                      isLoading={isLoadingMainData}
                                      isDisabled={isLoadingMainData}
                                      label={"Chart Type"}
                                      minWidth={200}
                                      maxWidth={225}
                                      onChange={setChartType}
                                      defaultValue={chartType}/>
                    {chartType.unit !== 'HISTORY' ? (
                        <VsiLabeledSelect options={allResourceOptions}
                                          isLoading={isLoadingMainData}
                                          isDisabled={isLoadingMainData}
                                          label={chartType.drop_desc}
                                          minWidth={200}
                                          maxWidth={800}
                                          onChange={setSelectedResource}
                                          defaultValue={selectedResource}/>
                    ) : null}
                    <VsiLabeledSelect options={nonRepeatAxis1Metrics}
                                      isLoading={isLoadingMainData}
                                      isDisabled={isLoadingMainData}
                                      label={`Left Axis`}
                                      minWidth={200}
                                      maxWidth={800}
                                      onChange={setAxis1Metric}
                                      defaultValue={axis1Metric}/>
                    <VsiLabeledSelect options={nonRepeatAxis2Metrics}
                                      isLoading={isLoadingMainData}
                                      isDisabled={isLoadingMainData}
                                      label={`Right Axis`}
                                      minWidth={200}
                                      maxWidth={800}
                                      onChange={setAxis2Metric}
                                      defaultValue={axis2Metric}/>
                </div>
                {chartType.unit !== 'HISTORY' && overflowTypeOptions.length ? (
                    <>
                        <VsiLabeledSelect options={overflowTypeOptions}
                                          isLoading={isLoadingMainData}
                                          isDisabled={isLoadingMainData}
                                          label={`${chartType.drop_desc}s`}
                                          minWidth={200}
                                          maxWidth={1200}
                                          onChange={setOverflowType}
                                          isMulti
                                          defaultValue={overflowType}/>
                        <Button style={{marginTop: "12px"}} onClick={applySavedSelection}
                                disabled={savedList.length < 1}
                                color={"primary"}
                                id="applyListTooltip">
                            {savedList.length > 0 ? 'Apply Saved Selection' : 'No Selection Saved'}
                        </Button>
                        {document.getElementById('applyListTooltip') && savedList.length > 0 ?
                            <Tooltip style={{color: styles[theme].textColor, backgroundColor: styles[theme].altBgColor}}
                                     isOpen={applyTooltipOpen}
                                     placement="right"
                                     target="applyListTooltip"
                                     toggle={() => {
                                         setApplyTooltipOpen(!applyTooltipOpen);
                                         setApplyTooltipMessage(`Apply Saved List of ${chartType.drop_desc}s`)
                                     }}>
                                {applyTooltipMessage}
                            </Tooltip> : null}
                        {overflowType.length > 0 && overflowType.length <= saveSelectionLimit ? (
                            <Button className={"h-50 d-inline-block"} style={{marginTop: "12px"}} onClick={savePlan}
                                    color={"success"}
                                    id="saveListTooltip">
                                <i className={`fa-solid fa-floppy-disk`}/>
                            </Button>) : <><VsiSpanText> Save up to {saveSelectionLimit}</VsiSpanText></>}
                        {document.getElementById('saveListTooltip') && overflowType.length > 0
                        && overflowType.length <= saveSelectionLimit ?
                            <Tooltip style={{color: styles[theme].textColor, backgroundColor: styles[theme].altBgColor}}
                                     isOpen={saveTooltipOpen}
                                     placement="right"
                                     target="saveListTooltip"
                                     toggle={() => {
                                         setSaveTooltipOpen(!saveTooltipOpen);
                                         setSaveTooltipMessage(`Save Selected List of ${chartType.drop_desc}s`)
                                     }}>
                                {saveTooltipMessage}
                            </Tooltip> : null}
                    </>
                ) : null}
            </VsiBubble>
            <VsiBubble>
                <Stockchart series={formattedChartData}
                            config={chartConfig}
                            animation={selectedResource.value !== "ALL"} // need to revise rerenders for ALL
                            height={600}/>
                {chartType.unit !== 'HISTORY' ? (
                    <VsiSmallText>
                        Recordset found: {`${allResourceOptions.length - 1}`},
                        Shows
                        selected: {`${formattedChartData.length}`} {`${axis2Metric.value !== 'none' ? '(Left&Right)' : ''}`}<br></br>
                    </VsiSmallText>
                ) : null}
            </VsiBubble>
            <VsiBubble>
                <Grid data={gridData} columnDefs={virtualTrends}/>
            </VsiBubble>
        </Card>
    );
}
